import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Wallet, TrendingUp, CreditCard, Users,
  ChevronLeft, ChevronRight, Menu, ArrowLeftRight,
  FileSpreadsheet, Loader2, LogOut, Cloud, CloudOff, Check
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useApp, fmtShort } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { exportFinanceData } from "../utils/exportExcel";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { label: "Budget Planner", icon: Wallet, path: "/dashboard/budget" },
  { label: "Net Worth", icon: TrendingUp, path: "/dashboard/net-worth" },
  { label: "Subscriptions", icon: CreditCard, path: "/dashboard/subscriptions" },
  { label: "You vs Average", icon: Users, path: "/dashboard/you-vs-average" },
];

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function formatMonth(key) {
  if (!key) return "";
  const [y, m] = key.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

function shiftMonth(key, delta) {
  const [y, m] = key.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function SidebarContent({ collapsed, setCollapsed, user, onSignOut }) {
  const location = useLocation();
  return (
    <div className="flex flex-col h-full">
      <div className={`flex items-center ${collapsed ? "justify-center px-3" : "px-5"} h-16 border-b border-white/5 flex-shrink-0`}>
        <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <TrendingUp className="w-3.5 h-3.5 text-slate-950" strokeWidth={2.5} />
        </div>
        {!collapsed && (
          <div className="ml-2.5 overflow-hidden">
            <p className="font-outfit font-bold text-white text-sm leading-tight whitespace-nowrap">Kara Finance</p>
            <p className="text-emerald-400 text-[10px] font-medium uppercase tracking-widest">Budget Pro</p>
          </div>
        )}
      </div>

      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => {
          const active = location.pathname === item.path || (item.path !== "/dashboard" && location.pathname.startsWith(item.path));
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 group ${
                active
                  ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                  : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
              } ${collapsed ? "justify-center" : ""}`}
            >
              <Icon className={`w-4 h-4 flex-shrink-0 ${active ? "text-emerald-400" : ""}`} />
              {!collapsed && <span className="truncate">{item.label}</span>}
              {!collapsed && active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400" />}
            </Link>
          );
        })}
      </nav>

      <div className={`px-2 py-4 border-t border-white/5 ${collapsed ? "flex flex-col items-center gap-2" : ""}`}>
        {!collapsed ? (
          <div className="space-y-1">
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/40 border border-white/5">
              <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-400 text-xs font-bold">
                  {user?.email?.[0]?.toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="text-slate-200 text-xs font-medium truncate">{user?.email || "My Finances"}</p>
                <p className="text-slate-500 text-[10px]">Personal Workspace</p>
              </div>
            </div>
            <button
              onClick={onSignOut}
              data-testid="sidebar-logout-button"
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/5 text-xs font-medium transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center" title={user?.email}>
              <span className="text-emerald-400 text-xs font-bold">
                {user?.email?.[0]?.toUpperCase() || "U"}
              </span>
            </div>
            <button
              onClick={onSignOut}
              title="Sign out"
              className="p-1.5 rounded-lg text-slate-600 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { state, dispatch, netWorth, saveStatus, totalIncome, totalNeeds, totalWants, totalSavings, leftover, savingsRate, totalAssets, totalLiabilities } = useApp();
  const { user, signOut } = useAuth();

  useEffect(() => { setMobileOpen(false); }, [location]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportFinanceData(state, {
        totalIncome, totalNeeds, totalWants, totalSavings,
        leftover, savingsRate, netWorth, totalAssets, totalLiabilities,
      });
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [exporting, state, totalIncome, totalNeeds, totalWants, totalSavings, leftover, savingsRate, netWorth, totalAssets, totalLiabilities]);

  const pageTitle = NAV.find((n) => n.path === location.pathname || (n.path !== "/dashboard" && location.pathname.startsWith(n.path)))?.label || "Dashboard";

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col flex-shrink-0 bg-slate-900/50 backdrop-blur-xl border-r border-white/5 transition-all duration-300 ${
          collapsed ? "w-[60px]" : "w-60"
        }`}
      >
        <SidebarContent collapsed={collapsed} setCollapsed={setCollapsed} user={user} onSignOut={handleSignOut} />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 flex-shrink-0 flex items-center justify-between px-4 md:px-6 bg-slate-950/80 backdrop-blur-md border-b border-white/5 z-30">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors" data-testid="mobile-sidebar-toggle">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-60 p-0 bg-slate-900 border-white/5">
                <SidebarContent collapsed={false} setCollapsed={() => {}} user={user} onSignOut={handleSignOut} />
              </SheetContent>
            </Sheet>
            {/* Collapse toggle desktop */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden md:flex p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-colors"
              data-testid="sidebar-collapse-toggle"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
            <h1 className="font-outfit font-semibold text-white text-base tracking-tight">{pageTitle}</h1>
          </div>

          <div className="flex items-center gap-2">
            {/* Save status indicator */}
            {saveStatus === "saving" && (
              <div className="hidden sm:flex items-center gap-1.5 text-slate-500 text-xs">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === "saved" && (
              <div className="hidden sm:flex items-center gap-1.5 text-emerald-500 text-xs">
                <Check className="w-3 h-3" />
                <span>Saved</span>
              </div>
            )}
            {saveStatus === "error" && (
              <div className="hidden sm:flex items-center gap-1.5 text-rose-400 text-xs">
                <CloudOff className="w-3 h-3" />
                <span>Save failed</span>
              </div>
            )}

            {/* Export to Excel button */}
            <button
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-excel-button"
              title="Export to Excel"
              className="flex items-center gap-1.5 bg-slate-800/50 hover:bg-emerald-500/10 border border-white/5 hover:border-emerald-500/30 text-slate-400 hover:text-emerald-400 text-xs font-medium rounded-lg px-3 py-1.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{exporting ? "Exporting…" : "Export"}</span>
            </button>

            {/* Net Worth ticker */}
            <div className="hidden sm:flex items-center gap-2 bg-slate-800/50 border border-white/5 rounded-lg px-3 py-1.5" data-testid="net-worth-ticker">
              <ArrowLeftRight className="w-3 h-3 text-slate-500" />
              <span className="text-slate-500 text-xs">Net Worth</span>
              <span className={`font-outfit font-bold text-sm tabular-nums ${netWorth >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {fmtShort(netWorth)}
              </span>
            </div>

            {/* Month selector */}
            <div className="flex items-center gap-1 bg-slate-800/50 border border-white/5 rounded-lg px-1 py-1" data-testid="month-selector">
              <button
                onClick={() => dispatch({ type: "SET_MONTH", payload: shiftMonth(state.currentMonth, -1) })}
                className="p-1 rounded text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="month-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-slate-200 text-xs font-medium px-2 min-w-[120px] text-center">
                {formatMonth(state.currentMonth)}
              </span>
              <button
                onClick={() => dispatch({ type: "SET_MONTH", payload: shiftMonth(state.currentMonth, 1) })}
                className="p-1 rounded text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="month-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* User email + Logout */}
            <div className="hidden md:flex items-center gap-2 pl-2 border-l border-white/5 ml-1">
              <span
                data-testid="user-email-display"
                className="text-slate-500 text-xs max-w-[140px] truncate"
                title={user?.email}
              >
                {user?.email}
              </span>
              <button
                onClick={handleSignOut}
                data-testid="logout-button"
                title="Sign out"
                className="flex items-center gap-1.5 bg-slate-800/50 hover:bg-rose-500/10 border border-white/5 hover:border-rose-500/30 text-slate-400 hover:text-rose-400 text-xs font-medium rounded-lg px-2.5 py-1.5 transition-all duration-200"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">Sign out</span>
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
